import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import AdminHeaderNavigation from '../../components/ui/AdminHeaderNavigation';
import BookingFilters from './components/OrderFilters';
import BookingTableRow from './components/OrderTableRow';
import BookingMobileCard from './components/OrderMobileCard';
import BookingDetailsModal from './components/OrderDetailsModal';
import BulkActionsBar from './components/BulkActionsBar';
import EmptyBookingsState from './components/EmptyOrdersState';
import { useAuth } from '../../components/context/AuthContext';
import Icon from '../../components/AppIcon';
import Pagination from '../../components/ui/Pagination';
import ExportReportModal from '../../components/ui/ExportReportModal';
import { generateOrdersReport } from '../../utils/reportGenerator';
import { downloadTextFile, escapeCsvValue, openPrintableReport } from '../../utils/exportUtils';
import repositoryContainer from '../../services/RepositoryContainer';
import RequestOrdersView from './components/RequestOrdersView';
import { useNotifications } from '../../components/context/NotificationContext';
import { computeManagementDateParams } from '../../utils/managementDateFilters';
import SegmentedControl from '../../components/ui/SegmentedControl';

const OrderManagement = () => {
  const navigate = useNavigate();
  const [managementView, setManagementView] = useState('orders');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [filters, setFilters] = useState({
    searchQuery: '',
    status: 'all',
    dateRange: 'upcoming',
    service: 'all',
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();
  const {
    recordRefreshVersion,
    newOrderCount,
    newRequestOrderCount,
    clearNewOrderCount,
    clearNewRequestOrderCount,
  } = useNotifications();
  const isAdmin = user?.roles?.some((role) => String(role).toLowerCase() === 'admin');

    const [isRestoring, setIsRestoring] = useState(false);

  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      await repositoryContainer.company.restoreBusiness();
      // Force a reload to refresh the user context and token from the backend
      window.location.reload(); 
    } catch (err) {
      console.error('Failed to restore business:', err);
      alert('Could not restore the business. Please contact support.');
      setIsRestoring(false);
    }
  };


if (user?.company && user.company.isActive === false) {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;700&display=swap');

        .archive-container {
          font-family: 'DM Sans', sans-serif;
          color: #fff;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          position: relative;
          background: #000;
        }

        .bg-overlay {
          position: absolute;
          inset: 0;
          background-image: url('https://weyyfslmfqjlkvtfwunm.supabase.co/storage/v1/object/public/Claw/Herosection/heroImage.png');
          background-size: cover;
          background-position: center;
          filter: blur(15px) brightness(0.4);
          opacity: 0.6;
          z-index: 0;
        }

        .archive-card {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 480px;
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 48px 32px;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }

        .status-icon-wrap {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .archive-badge {
          display: inline-block;
          padding: 4px 12px;
          background: rgba(234, 179, 8, 0.1);
          border: 1px solid rgba(234, 179, 8, 0.3);
          color: #eab308;
          border-radius: 99px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 20px;
        }

        .archive-title {
          font-family: 'DM Serif Display', serif;
          font-size: 32px;
          margin-bottom: 12px;
          letter-spacing: -0.02em;
        }

        .archive-desc {
          font-size: 15px;
          color: rgba(255, 255, 255, 0.6);
          line-height: 1.6;
          margin-bottom: 32px;
          font-weight: 300;
        }

        .info-row {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 12px;
          padding: 16px;
          text-align: left;
          margin-bottom: 32px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .info-item {
          display: flex;
          gap: 12px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 8px;
        }

        .restore-btn {
          width: 100%;
          background: #fff;
          color: #000;
          border: none;
          padding: 16px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 12px;
        }

        .restore-btn:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-2px);
        }

        .signout-btn {
          width: 100%;
          background: transparent;
          color: rgba(255, 255, 255, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 16px;
          border-radius: 12px;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .signout-btn:hover {
          background: rgba(255, 255, 255, 0.05);
          color: #fff;
        }
      `}</style>

      <div className="archive-container">
        <div className="bg-overlay" />
        
        <div className="archive-card">
          <div className="status-icon-wrap">
            <Icon name="Archive" size={32} color="rgba(255,255,255,0.7)" />
          </div>

          <div className="archive-badge">Status: Archived</div>
          
          <h1 className="archive-title">Workspace Offline</h1>
          <p className="archive-desc">
            Your business profile for <strong>{user?.company?.name || 'your store'}</strong> has been deactivated. All public booking services and dashboard tools are currently restricted.
          </p>

          <div className="info-row">
            <div className="info-item">
              <Icon name="EyeOff" size={16} color="#eab308" />
              <span>Public booking page is hidden</span>
            </div>
            <div className="info-item">
              <Icon name="Database" size={16} color="#eab308" />
              <span>Data is preserved for compliance</span>
            </div>
            <div className="info-item">
              <Icon name="HelpCircle" size={16} color="#eab308" />
              <span>Contact support@clawappz.com</span>
            </div>
          </div>

          <button className="restore-btn" onClick={handleRestore} disabled={isRestoring}>
            {isRestoring ? 'Processing Restoration...' : 'Reactivate Business'}
          </button>
          
          <button className="signout-btn" onClick={logout}>
            Log Out
          </button>
        </div>
      </div>
    </>
  );
}

  const mockOrders = [
    // {
    //   id: 1005,
    //   clientName: "+1 (555) 567-8901",
    //   date: "2025-01-08",
    //   status: "pending",
    //   history: [
    //     { action: "Order created", timestamp: "2024-12-31T08:30:00" }
    //   ],
    //   items: [
    //     {  name: "Classic Manicure", price: 45, quantity: 2 },
    //     {  name: "Nail Art Design", price: 20, quantity: 1 },
    //     {  name: "Nail Art Design", price: 20, quantity: 1 },
    //     {  name: "Nail Art Design", price: 20, quantity: 1 },
    //     {  name: "Nail Art Design", price: 20, quantity: 1 },
    //     {  name: "Nail Art Design", price: 20, quantity: 1 },
    //     {  name: "Nail Art Design", price: 20, quantity: 1 }
    //   ]
    // },
    // {
    //   id: 1006,
    //   clientName: "+1 (555) 678-9012",
    //   date: "2025-01-09",
    //   status: "confirmed",
    //   history: [
    //     { action: "Order confirmed", timestamp: "2024-12-31T09:10:00" }
    //   ],
    //   items: [
    //     { name: "Gel Polish", price: 30, quantity: 1 }
    //   ]
    // }
  ];

  // Admin: Create order
const createOrder = async (orderData) => {
  try {
    const response = await repositoryContainer.order.create({
      clientName: orderData.clientName,
      clientPhone: orderData.clientPhone,
      items: orderData.items.map(item => ({
        serviceItemId: item.productId,
        quantity: item.quantity
      }))
    });
    return response;
  } catch (error) {
    console.error('Failed to create order:', error);
  }
};

const PAGE_SIZE = 20;
const [page, setPage] = useState(1);
const [totalCount, setTotalCount] = useState(0);
const [totalPages, setTotalPages] = useState(0);

const updateOrderStatus = async (id, status) => {
  try {
    return await repositoryContainer.order.updateStatus(id, status);
  } catch (err) {
    console.error('Failed to update order status', err);
  }
};

const loadOrders = async (pageNum = 1) => {
  setLoading(true);
  setError(null);
  const { startDate, endDate } = computeManagementDateParams(filters);
  try {
    const raw = await repositoryContainer.order.listPaged({
      page: pageNum,
      pageSize: PAGE_SIZE,
      status: filters.status !== 'all' ? filters.status : undefined,
      startDate,
      endDate,
    });
    const data = raw?.response ?? raw;
    setBookings(data.items ?? []);
    setSelectedBooking((current) => {
      if (!current) return current;
      const refreshed = (data.items ?? []).find((order) => order.id === current.id);
      return refreshed ? { ...current, ...refreshed } : current;
    });
    setTotalCount(data.totalCount ?? 0);
    setTotalPages(data.totalPages ?? 0);
    setPage(pageNum);
  } catch (err) {
    setError(err);
    setBookings([]);
  } finally {
    setLoading(false);
  }
};

const bulkUpdateOrderStatus = async (ids, status) => {
  try {
    return await repositoryContainer.order.bulkUpdateStatus(ids, status);
  } catch (error) {
    console.error('Failed to bulk update order statuses:', error);
  }
};

const handlePageChange = (p) => loadOrders(p);

useEffect(() => {
  loadOrders(1);
}, [filters.status, filters.dateRange, filters.startDate, filters.endDate]);

useEffect(() => {
  if (recordRefreshVersion > 0 && managementView === 'orders') loadOrders(page);
}, [managementView, recordRefreshVersion]);

useEffect(() => {
  if (managementView === 'orders' && newOrderCount > 0) clearNewOrderCount();
  if (managementView === 'requests' && newRequestOrderCount > 0) clearNewRequestOrderCount();
}, [
  clearNewOrderCount,
  clearNewRequestOrderCount,
  managementView,
  newOrderCount,
  newRequestOrderCount,
]);
  
  const enrichOrders = (orders) =>
    orders.map(order => {
      const totalAmount = order.items?.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
  
      return {
        ...order,
        totalAmount,
        itemCount: order.items?.length ?? 0,
        serviceName: order.items?.map(i => i.itemName ?? i.name).join(', ')
      };
    });
  
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);

  useEffect(() => {
    filterAndSortBookings();
  }, [filters, sortConfig, bookings]);

  const filterAndSortBookings = () => {
    let filtered = enrichOrders([...bookings]);

    if (filters?.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        b?.clientName?.toLowerCase()?.includes(query) ||
        b?.clientEmail?.toLowerCase()?.includes(query) ||
        b?.clientPhone?.includes(query)
      );
    }

    filtered.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];
      if (sortConfig?.key === 'date') {
        aValue = new Date(a.date);
        bValue = new Date(b.date);
      }
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredBookings(filtered);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = (query) => {
    setFilters(prev => ({ ...prev, searchQuery: query }));
  };

  const handleResetFilters = () => {
    setFilters({
      searchQuery: '',
      status: 'all',
      dateRange: 'upcoming',
      service: 'all',
      startDate: '',
      endDate: ''
    });
  };

  const [showExportModal, setShowExportModal] = useState(false);

  const handleExport = () => setShowExportModal(true);

  const doExportCSV = () => {
    const calculateItemTotal = (item) => {
      const unitPrice = Number(item?.unitPrice ?? item?.price ?? 0);
      const quantity = Number(item?.quantity ?? 0);
      return unitPrice * quantity;
    };

    const calculateOrderQuantity = (order) =>
      order?.items?.reduce((sum, item) => sum + Number(item?.quantity ?? 0), 0) ?? 0;

    const calculateOrderTotal = (order) =>
      order?.items?.reduce((sum, item) => sum + calculateItemTotal(item), 0) ?? 0;

    const rows = filteredBookings?.map((order) => {
      const itemsDetail = order?.items?.map((item) =>
        `${item?.itemName ?? item?.name ?? 'Item'} x${Number(item?.quantity ?? 0)}`
      )?.join('; ') || '';

      return [
        order?.id,
        order?.clientName,
        order?.clientEmail,
        order?.clientPhone,
        itemsDetail,
        calculateOrderQuantity(order),
        `GH¢${calculateOrderTotal(order).toFixed(2)}`,
        order?.date,
        order?.status
      ];
    }) ?? [];

    const totalOrders = rows.length;
    const totalItems = filteredBookings?.reduce(
      (sum, order) => sum + calculateOrderQuantity(order),
      0
    ) ?? 0;
    const totalRevenue = filteredBookings?.reduce(
      (sum, order) => sum + calculateOrderTotal(order),
      0
    ) ?? 0;

    const csvRows = [
      ['ID', 'Client Name', 'Email', 'Phone', 'Items', 'Quantity', 'Total', 'Date', 'Status'],
      ...rows
    ];

    if (totalOrders > 0) {
      csvRows.push([]);
      csvRows.push([
        'Totals',
        '',
        '',
        '',
        '',
        totalItems,
        `GH¢${totalRevenue.toFixed(2)}`,
        '',
        `Orders: ${totalOrders}`
      ]);
    }

    const csvContent = csvRows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');

    downloadTextFile({
      content: csvContent,
      fileName: `orders-${new Date().toISOString().split('T')[0]}.csv`,
      mimeType: 'text/csv;charset=utf-8',
      includeBom: true,
    });
  };

  const doExportJSON = () => {
    const calculateItemTotal = (item) =>
      Number(item?.unitPrice ?? item?.price ?? 0) * Number(item?.quantity ?? 0);

    const data = filteredBookings?.map(order => ({
      id: order?.id,
      clientName: order?.clientName,
      clientEmail: order?.clientEmail,
      clientPhone: order?.clientPhone,
      items: order?.items?.map(i => ({
        name: i?.itemName ?? i?.name,
        quantity: i?.quantity,
        price: i?.unitPrice ?? i?.price,
      })),
      total: order?.items?.reduce((s, i) => s + calculateItemTotal(i), 0) ?? 0,
      date: order?.date,
      status: order?.status,
    })) ?? [];

    downloadTextFile({
      content: JSON.stringify(data, null, 2),
      fileName: `orders-${new Date().toISOString().split('T')[0]}.json`,
      mimeType: 'application/json;charset=utf-8',
    });
  };

  const doExportPDF = () => {
    const html = generateOrdersReport(filteredBookings, filters, user?.company?.name);
    openPrintableReport(html);
  };

  const handleDoExport = (format) => {
    if (format === 'pdf')  doExportPDF();
    else if (format === 'json') doExportJSON();
    else doExportCSV();
  };

  const handleSelectBooking = (id, checked) => {
    setSelectedBookings(prev =>
      checked ? [...prev, id] : prev?.filter(bookingId => bookingId !== id)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedBookings(checked ? filteredBookings?.map(b => b?.id) : []);
  };

  const handleBulkAction = async (action) => {
  if (action === 'sendreminder') {
    setSelectedBookings([]);
    return;
  }

  if (action && selectedBookings.length > 0) {
    try {
      const result = await bulkUpdateOrderStatus(selectedBookings, action);

      if (result?.failed > 0) {
        console.warn(`${result.succeeded} updated, ${result.failed} failed`);
      }

      loadOrders(page);
    } catch (err) {
      console.error('Bulk order update failed', err);
    }
  }

  setSelectedBookings([]);
};

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await updateOrderStatus(bookingId, newStatus);
      await loadOrders(page);
      setSelectedBooking(prev => prev?.id === bookingId ? { ...prev, status: newStatus } : prev);
    } catch (err) {
      console.error('Failed to change order status', err);
      setBookings(prev => prev?.map(b =>
        b?.id === bookingId ? { ...b, status: newStatus } : b
      ));
      setSelectedBooking(prev => prev?.id === bookingId ? { ...prev, status: newStatus } : prev);
    }
  };

  const handlePaymentRecorded = async (orderId, paymentResult) => {
    setBookings((current) => current.map((order) => (
      order.id === orderId ? { ...order, ...paymentResult } : order
    )));
    setSelectedBooking((current) => (
      current?.id === orderId ? { ...current, ...paymentResult } : current
    ));
    const [details] = await Promise.all([
      repositoryContainer.order.getById(orderId).catch(() => null),
      loadOrders(page),
    ]);
    const normalizedDetails = details?.response ?? details;
    if (normalizedDetails) setSelectedBooking(normalizedDetails);
  };

  const handleContact = (booking) => {
    window.location.href = `tel:${booking?.clientPhone}`;
  };

  const handleLogout = () => {
    navigate('/date-selection');
  };

  const hasActiveFilters = filters?.searchQuery || filters?.status !== 'all' || 
                          filters?.dateRange !== 'upcoming' || filters?.service !== 'all';

  const allSelected = filteredBookings?.length > 0 && 
                     selectedBookings?.length === filteredBookings?.length;

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={`transition-all duration-250 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <AdminHeaderNavigation
          userName="Admin User"
          onLogout={handleLogout}
          isSidebarCollapsed={isSidebarCollapsed}
        />

        <main className="pt-24 px-4 md:px-6 lg:px-8 pb-24">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="mb-2 font-heading text-3xl font-bold text-foreground md:text-4xl">
                  Order Management
                </h1>
                <p className="text-muted-foreground font-caption">
                  {managementView === 'orders'
                    ? (isAdmin ? 'View and manage all client orders' : 'View all client orders')
                    : 'Review customer service and product requests'}
                </p>
              </div>
              <SegmentedControl
                value={managementView}
                onChange={setManagementView}
                ariaLabel="Order type"
                className="w-full flex-shrink-0 sm:w-auto"
                options={[
                  { value: 'orders', label: 'Regular Orders', badge: newOrderCount },
                  { value: 'requests', label: 'Request Orders', badge: newRequestOrderCount },
                ]}
              />
            </div>

            {managementView === 'orders' ? (
              <>
            <BookingFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onReset={handleResetFilters}
              onExport={handleExport}
            />

            {filteredBookings?.length === 0 ? (
              <div className="bg-card rounded-xl border border-border">
                <EmptyBookingsState
                  hasFilters={hasActiveFilters}
                  onResetFilters={handleResetFilters}
                />
              </div>
            ) : (
              <>
                <div className="hidden lg:block bg-card rounded-xl border border-border overflow-hidden">
                  <div className="overflow-auto max-h-[calc(100vh-360px)] custom-scrollbar">
                    <table className="w-full">
                    <thead className="bg-muted border-b border-border sticky top-0 z-10">
                        <tr>
                          {/* Select all */}
                          {isAdmin && (
                            <th className="px-4 py-4 text-left">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                              />
                            </th>
                          )}

                          {/* Client */}
                          <th
                            className="px-4 py-4 text-left"
                            aria-sort={
                              sortConfig?.key === 'clientName'
                                ? sortConfig.direction === 'asc'
                                  ? 'ascending'
                                  : 'descending'
                                : 'none'
                            }
                          >
                            <button
                              onClick={() => handleSort('clientName')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Client
                              {sortConfig?.key === 'clientName' && (
                                <Icon
                                  name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                  size={16}
                                />
                              )}
                            </button>
                          </th>

                          {/* Phone (no sort) */}
                          <th className="px-4 py-4 text-left">
                            <span className="font-caption font-semibold text-sm text-foreground">
                              Phone
                            </span>
                          </th>

                          {/* Service */}
                          <th
                            className="px-4 py-4 text-left"
                            aria-sort={
                              sortConfig?.key === 'serviceName'
                                ? sortConfig.direction === 'asc'
                                  ? 'ascending'
                                  : 'descending'
                                : 'none'
                            }
                          >
                            <button
                              onClick={() => handleSort('serviceName')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Items
                              {sortConfig?.key === 'serviceName' && (
                                <Icon
                                  name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                  size={16}
                                />
                              )}
                            </button>
                          </th>

                          {/* Total */}
                          <th
                            className="px-4 py-4 text-left"
                            aria-sort={
                              sortConfig?.key === 'date'
                                ? sortConfig.direction === 'asc'
                                  ? 'ascending'
                                  : 'descending'
                                : 'none'
                            }
                          >
                            <button
                              onClick={() => handleSort('date')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Total
                              {sortConfig?.key === 'date' && (
                                <Icon
                                  name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                  size={16}
                                />
                              )}
                            </button>
                          </th>

                          {/* Date */}
                          <th className="px-4 py-4 text-left">
                            <span className="font-caption font-semibold text-sm text-foreground">
                              Date
                            </span>
                          </th>

                          {/* Status */}
                          <th
                            className="px-4 py-4 text-left"
                            aria-sort={
                              sortConfig?.key === 'status'
                                ? sortConfig.direction === 'asc'
                                  ? 'ascending'
                                  : 'descending'
                                : 'none'
                            }
                          >
                            <button
                              onClick={() => handleSort('status')}
                              className="flex items-center gap-2 font-caption font-semibold text-sm text-foreground hover:text-primary transition-colors"
                            >
                              Status
                              {sortConfig?.key === 'status' && (
                                <Icon
                                  name={sortConfig.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                                  size={16}
                                />
                              )}
                            </button>
                          </th>

                          {/* Actions */}
                          <th className="px-4 py-4 text-left">
                            <span className="font-caption font-semibold text-sm text-foreground">
                              Actions
                            </span>
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredBookings?.map(booking => (
                          <BookingTableRow
                            key={booking?.id}
                            booking={booking}
                            isSelected={selectedBookings?.includes(booking?.id)}
                            onSelect={handleSelectBooking}
                            canSelect={isAdmin}
                            onViewDetails={setSelectedBooking}
                            onContact={handleContact}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="lg:hidden space-y-4">
                  {filteredBookings?.map(booking => (
                    <BookingMobileCard
                      key={booking?.id}
                      booking={booking}
                      isSelected={selectedBookings?.includes(booking?.id)}
                      onSelect={handleSelectBooking}
                      canSelect={isAdmin}
                      onViewDetails={setSelectedBooking}
                      onContact={handleContact}
                    />
                  ))}
                </div>

                <Pagination
                  page={page}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  pageSize={PAGE_SIZE}
                  onPageChange={handlePageChange}
                  isLoading={loading}
                />
              </>
            )}
              </>
            ) : (
              <RequestOrdersView />
            )}
          </div>
        </main>

        {managementView === 'orders' && (
          <>
            {isAdmin && (
              <BulkActionsBar
                selectedCount={selectedBookings?.length}
                onBulkAction={handleBulkAction}
                onClearSelection={() => setSelectedBookings([])}
              />
            )}

            {selectedBooking && (
              <BookingDetailsModal
                booking={selectedBooking}
                onClose={() => setSelectedBooking(null)}
                onStatusChange={handleStatusChange}
                onPaymentRecorded={handlePaymentRecorded}
              />
            )}

            <ExportReportModal
              isOpen={showExportModal}
              onClose={() => setShowExportModal(false)}
              onExport={handleDoExport}
              recordCount={filteredBookings?.length ?? 0}
              title="Orders"
              filters={filters}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OrderManagement;
